/**
 * Pure, domain-agnostic helpers for the tri-state hierarchical multiselect.
 *
 * Data model:
 *   nodes:  Group[]
 *   Group:  { key, label, parents: Parent[] }
 *   Parent: { id, label, children?: Child[] }
 *   Child:  { id, label }
 *
 * The entire selection state is a flat list of selected LEAF ids. A parent with
 * no children is its own leaf. Tri-state is always DERIVED from this list.
 */

/** Leaf ids that a parent owns: its children's ids, or itself when childless. */
export function leafIdsOfParent(parent) {
    return parent.children && parent.children.length > 0 ? parent.children.map((child) => child.id) : [parent.id];
}

/** Every selectable leaf id across all groups. */
export function collectLeafIds(nodes) {
    const ids = [];
    (nodes || []).forEach((group) => {
        (group.parents || []).forEach((parent) => {
            leafIdsOfParent(parent).forEach((id) => ids.push(id));
        });
    });
    return ids;
}

/** Derive a parent's tri-state from the selected-leaf set. */
export function getParentState(parent, selectedSet) {
    const leaves = leafIdsOfParent(parent);
    const selectedCount = leaves.filter((id) => selectedSet.has(id)).length;

    if (selectedCount === 0) {
        return 'unchecked';
    }
    if (selectedCount === leaves.length) {
        return 'checked';
    }
    return 'indeterminate';
}

/** True when every leaf in the tree is selected (and there is at least one leaf). */
export function isAllSelected(value, nodes) {
    const allLeafIds = collectLeafIds(nodes);
    if (allLeafIds.length === 0) {
        return false;
    }
    const selectedSet = new Set(value);
    return allLeafIds.every((id) => selectedSet.has(id));
}

/** Toggle a whole parent: select all its leaves, or clear them all. */
export function toggleParent(value, parent) {
    const leaves = leafIdsOfParent(parent);
    const selectedSet = new Set(value);
    const allSelected = leaves.every((id) => selectedSet.has(id));

    if (allSelected) {
        const remove = new Set(leaves);
        return value.filter((id) => !remove.has(id));
    }

    return Array.from(new Set([...value, ...leaves]));
}

/** Toggle a single leaf id. */
export function toggleLeaf(value, leafId) {
    return value.includes(leafId) ? value.filter((id) => id !== leafId) : [...value, leafId];
}

function matches(text, query) {
    return String(text).toLowerCase().includes(query);
}

/**
 * Filter the tree for a search query. Matching a parent keeps all its children;
 * matching a child keeps it under its parent. Returns the parents that should be
 * auto-expanded to reveal matches.
 */
export function filterNodes(nodes, query) {
    const q = (query || '').trim().toLowerCase();

    if (!q) {
        return { groups: nodes || [], autoExpand: new Set() };
    }

    const autoExpand = new Set();

    const groups = (nodes || [])
        .map((group) => {
            const parents = (group.parents || [])
                .map((parent) => {
                    const children = parent.children || [];

                    if (matches(parent.label, q)) {
                        if (children.length > 0) {
                            autoExpand.add(parent.id);
                        }
                        return parent;
                    }

                    const matchingChildren = children.filter((child) => matches(child.label, q));
                    if (matchingChildren.length > 0) {
                        autoExpand.add(parent.id);
                        return { ...parent, children: matchingChildren };
                    }

                    return null;
                })
                .filter(Boolean);

            return { ...group, parents };
        })
        .filter((group) => group.parents.length > 0);

    return { groups, autoExpand };
}
